import { useState, useEffect } from 'react';

/**
 * Custom hook for detecting device type and handling responsive layouts
 * @returns {Object} Object containing responsive state and functions
 */
export const useResponsive = () => {
  const [isOnPhone, setIsOnPhone] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const [navExpanded, setNavExpanded] = useState(true);
  const [userClosedNav, setUserClosedNav] = useState(false);

  // Detect if user is on a phone
  useEffect(() => {
    const detectPhone = () => {
      // Method 1: Screen size detection
      const isMobileBySize = window.innerWidth < 550;

      // Method 2: User agent detection (not always reliable but helpful as a secondary check)
      const isMobileByAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

      // Method 3: Touch capability detection (most phones have touch screens)
      const isMobileByTouch = 'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0;

      // Method 4: Device orientation capability (most phones have this)
      const hasOrientationSupport = Boolean(window.DeviceOrientationEvent);

      // Combine signals to make a decision
      // More conservative approach: require screen size AND at least one other indicator
      const isPhone = isMobileBySize && (isMobileByAgent || isMobileByTouch || hasOrientationSupport);

      setIsOnPhone(isPhone);
    };

    // Set initial value on component mount
    detectPhone();

    // Add event listener to detect orientation or size changes
    window.addEventListener('resize', detectPhone);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('resize', detectPhone);
    };
  }, []); // Empty dependency array since we only want this to run once on mount

  // Handle responsive nav toggle based on screen size
  useEffect(() => {
    const handleResize = () => {
      // Set breakpoint - adjust the pixel value as needed for your design
      const breakpoint = 850; // Common breakpoint for mobile devices
      const currentWidth = window.innerWidth;

      // Store previous window width to detect crossing the breakpoint
      const prevWidth = window.prevWidth || 0;
      window.prevWidth = currentWidth;

      // Update forceClose based on window width
      setForceClose(currentWidth < breakpoint);

      // If window is too small, close the nav
      if (currentWidth < breakpoint && navExpanded) {
        setNavExpanded(false);
      }

      // If window was previously below breakpoint and now above it, reopen the nav
      // Only reopen if it wasn't manually closed by the user when screen was large
      if (prevWidth < breakpoint && currentWidth >= breakpoint && !navExpanded && !userClosedNav) {
        setNavExpanded(true);
      }
    };

    // Set initial value on component mount
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navExpanded, userClosedNav]); // Added userClosedNav to dependencies

  // Function to toggle nav expansion state
  const toggleNavExpanded = () => {
    setNavExpanded(!navExpanded);
    setUserClosedNav(!navExpanded); // If we're opening, we're not "user closed" anymore
  };

  return {
    isOnPhone,
    forceClose,
    navExpanded,
    userClosedNav,
    toggleNavExpanded,
  };
};